import React, { useCallback, useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ShadowDomWrapper,
  generateConciergeCSSFromWidgetTheme,
} from "@brandclub/common-ui";
import {
  SearchPanelWidget,
  SearchPanelProduct,
  useWidgetTheme,
} from "@brandclub/bc-widgets";
import { HOST_DOMAIN } from "../../utils/domain";
import {
  searchProductsByText,
  fetchSearchTermAutoCompleteSuggestions,
} from "../../api/rest/search";
import { useBrandAppConfigWithFallback } from "../../api/cache/appConfigQueryOptions";

const API_BASE_URL = `${HOST_DOMAIN}/api-rewards`;

export interface SearchButtonProps {
  isConciergeEnabled?: boolean;
}

export const SearchButton: React.FC<SearchButtonProps> = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  const { data: brandAppConfig } = useBrandAppConfigWithFallback();
  const brandId = brandAppConfig?.brandIds?.[0];

  // Theme from bc-widgets for CSS generation in shadow DOM
  const { data: widgetThemeData } = useWidgetTheme();
  const themeCSS = useMemo(
    () => generateConciergeCSSFromWidgetTheme(widgetThemeData),
    [widgetThemeData]
  );

  // --- Data: suggested questions (brand-level, same as concierge welcome screen) ---
  const { data: suggestedQueriesData, isLoading: isLoadingQuestions } =
    useQuery({
      queryKey: ["searchPanel", "suggestedQueries", brandId],
      queryFn: async () => {
        const url = new URL(`${API_BASE_URL}/concierge/suggestedQueries`);
        if (brandId) url.searchParams.set("brandId", brandId.toString());
        const res = await fetch(url.toString());
        if (res.ok) {
          const data = await res.json();
          return (
            (data?.suggestedQuestions as { query: string }[])?.map(
              (q) => q.query
            ) || []
          );
        }
        return [];
      },
      enabled: isOpen && !!brandId,
      staleTime: 5 * 60 * 1000,
    });

  const conciergeQuestions: string[] = (suggestedQueriesData || []).slice(0, 5);

  // --- Data: popular products (initial load — empty search) ---
  const { data: popularProductsData, isLoading: isLoadingProducts } = useQuery({
    queryKey: ["searchPanel", "popularProducts", brandId],
    queryFn: async () => {
      const res = await searchProductsByText("", {
        brandIds: brandId ? [brandId] : undefined,
        page: { start: 0, size: 6 },
      });
      return res.products || [];
    },
    enabled: isOpen && !!brandId,
    staleTime: 5 * 60 * 1000,
  });

  // --- Data: trending searches ---
  const { data: trendingSearchesData, isLoading: isLoadingTrending } = useQuery(
    {
      queryKey: ["searchPanel", "trendingSearches", brandId],
      queryFn: async () => {
        const res = await fetchSearchTermAutoCompleteSuggestions({
          searchText: "",
          brandId: brandId || undefined,
        });
        return res.searchTerms?.map((t) => t.searchTerm).slice(0, 7) || [];
      },
      enabled: isOpen && !!brandId,
      staleTime: 5 * 60 * 1000,
    }
  );

  // --- Data: search results (after user submits) ---
  const [searchResultProducts, setSearchResultProducts] = useState<
    SearchPanelProduct[]
  >([]);
  const [isSearching, setIsSearching] = useState(false);

  // Map API products to SearchPanelProduct format
  const mapProducts = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (products: any[]): SearchPanelProduct[] =>
      products.map((p) => ({
        name: p.title || p.name || "",
        price: p.retailPrice ? `$${Number(p.retailPrice).toFixed(2)}` : "",
        imageUrl: p.imageUrls?.[0] || p.imageUrl || p.mainImageUrl || "",
        onClick: () => {
          if (p.brandclubProductUrl) {
            window.open(p.brandclubProductUrl, "_blank");
          }
        },
      })),
    []
  );

  const popularProducts: SearchPanelProduct[] = useMemo(
    () =>
      hasSearched
        ? searchResultProducts
        : mapProducts(popularProductsData || []),
    [hasSearched, searchResultProducts, popularProductsData, mapProducts]
  );

  const trendingSearches: string[] = useMemo(
    () => trendingSearchesData || [],
    [trendingSearchesData]
  );

  // --- Handlers ---
  const handleOpen = useCallback(() => setIsOpen(true), []);
  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSearchQuery("");
    setHasSearched(false);
    setSearchResultProducts([]);
  }, []);

  const handleSearchSubmit = useCallback(
    async (query: string) => {
      if (!query.trim()) return;
      setHasSearched(true);
      setIsSearching(true);
      try {
        const res = await searchProductsByText(query, {
          brandIds: brandId ? [brandId] : undefined,
          page: { start: 0, size: 6 },
        });
        setSearchResultProducts(mapProducts(res.products || []));
      } catch {
        setSearchResultProducts([]);
      } finally {
        setIsSearching(false);
      }
    },
    [brandId, mapProducts]
  );

  const handleAskConcierge = useCallback(
    (query: string) => {
      // Navigate to the concierge page with the query pre-filled. Serve the
      // server-computed canonical URL when present (D-23 — the brand may host
      // the concierge on their Shopify store); HOST_DOMAIN is the fallback arm.
      const base =
        brandAppConfig?.canonicalConciergeUrl ?? `${HOST_DOMAIN}/concierge`;
      const conciergeUrl = `${base}${
        query ? `?q=${encodeURIComponent(query)}` : ""
      }`;
      window.open(conciergeUrl, "_blank");
    },
    [brandAppConfig?.canonicalConciergeUrl]
  );

  const handleQuestionClick = useCallback(
    (question: string) => {
      handleAskConcierge(question);
    },
    [handleAskConcierge]
  );

  const handleProductClick = useCallback((product: SearchPanelProduct) => {
    product.onClick?.();
  }, []);

  const handleTrendingClick = useCallback(
    (search: string) => {
      setSearchQuery(search);
      handleSearchSubmit(search);
    },
    [handleSearchSubmit]
  );

  // Brand logo element — use checkout page logo or membership logo as fallback
  const logoUrl =
    brandAppConfig?.checkoutPageConfig?.logoPicture ||
    brandAppConfig?.membershipPageConfig?.logoPicture;

  const brandLogo = logoUrl ? (
    <img
      src={logoUrl}
      alt={brandAppConfig?.appName || ""}
      style={{ height: "32px", objectFit: "contain" }}
    />
  ) : undefined;

  return (
    <>
      {/* Trigger icon (closed state) — rendered directly into the .bc-search slot */}
      {!isOpen && (
        <SearchPanelWidget
          widgetId="dtc-search-panel"
          displayName="Search Panel"
          isOpen={false}
          searchQuery={searchQuery}
          isLoading={false}
          hasSearched={false}
          conciergeQuestions={[]}
          popularProducts={[]}
          trendingSearches={[]}
          onOpen={handleOpen}
          onClose={handleClose}
          onSearchChange={setSearchQuery}
          onSearchSubmit={handleSearchSubmit}
          onAskConcierge={handleAskConcierge}
          onQuestionClick={handleQuestionClick}
          onProductClick={handleProductClick}
          onTrendingClick={handleTrendingClick}
        />
      )}

      {/* Search panel overlay — rendered at body level via fixed positioning */}
      {isOpen && (
        <ShadowDomWrapper themeCSS={themeCSS} hostId="search-panel-shadow-host">
          <SearchPanelWidget
            widgetId="dtc-search-panel"
            displayName="Search Panel"
            isOpen={isOpen}
            searchQuery={searchQuery}
            isLoading={
              isSearching ||
              (!hasSearched &&
                (isLoadingQuestions || isLoadingProducts || isLoadingTrending))
            }
            hasSearched={hasSearched}
            conciergeQuestions={conciergeQuestions}
            popularProducts={popularProducts}
            trendingSearches={trendingSearches}
            onOpen={handleOpen}
            onClose={handleClose}
            onSearchChange={setSearchQuery}
            onSearchSubmit={handleSearchSubmit}
            onAskConcierge={handleAskConcierge}
            onQuestionClick={handleQuestionClick}
            onProductClick={handleProductClick}
            onTrendingClick={handleTrendingClick}
            brandLogo={brandLogo}
          />
        </ShadowDomWrapper>
      )}
    </>
  );
};
